/** The Moving Supplies native planner.
 * Planning quantities preserve the original estimator rules. Prices and variant
 * IDs come exclusively from real products selected in the Shopify theme editor.
 */
(() => {
  if (window.TMSNativeMove) { window.TMSNativeMove.mount(document); return; }
  const KEYS = ['smallBoxes', 'mediumBoxes', 'largeBoxes', 'wardrobeBoxes', 'tapeRolls', 'paperBundles', 'bubbleRolls', 'tapeDispenser'];
  const SPECIAL = ['booksAndHeavy', 'dishwareGlassware', 'hangingWardrobe', 'electronicsScreens', 'mirrorsArtwork'];
  const BASE = {
    room: [10, 8, 4, 1, 2, 1, 1, 1], studio: [12, 10, 6, 1, 3, 1, 1, 1],
    apartment_1bed: [15, 14, 8, 2, 3, 1, 1, 1], apartment_2bed: [22, 18, 12, 3, 4, 2, 2, 1],
    house_3bed: [32, 28, 18, 4, 6, 3, 2, 1], house_4plus: [45, 38, 25, 6, 8, 4, 3, 1],
    office: [25, 20, 10, 0, 5, 2, 2, 1]
  };
  const STORAGE = { draft: 'tms.native.draft.v2', plan: 'tms.native.plan.v2', details: 'tms.native.details.v2' };
  const instances = new Map();
  const number = (value) => Math.min(9999, Math.max(0, Math.floor(Number(value) || 0)));
  function sanitize(value = {}) {
    if (!value || typeof value !== 'object') value = {};
    const input = { moveType: Object.hasOwn(BASE, value.moveType) ? value.moveType : 'apartment_1bed', density: ['few', 'normal', 'many'].includes(value.density) ? value.density : 'normal', specialItems: {}, existingSupplies: {}, withPet: value.withPet === true };
    SPECIAL.forEach((key) => { input.specialItems[key] = value.specialItems?.[key] === true; });
    KEYS.forEach((key) => { input.existingSupplies[key] = number(value.existingSupplies?.[key]); });
    return input;
  }
  function calculate(value) {
    const input = sanitize(value);
    const factor = input.density === 'few' ? 0.8 : input.density === 'many' ? 1.3 : 1;
    const gross = Object.fromEntries(KEYS.map((key, index) => [key, key === 'tapeDispenser' ? 1 : Math.round(BASE[input.moveType][index] * factor)]));
    if (input.specialItems.booksAndHeavy) gross.smallBoxes += Math.round(8 * factor);
    if (input.specialItems.dishwareGlassware) { gross.mediumBoxes += Math.round(4 * factor); gross.paperBundles += 1; }
    if (input.specialItems.hangingWardrobe) gross.wardrobeBoxes += Math.round(2 * factor);
    if (input.specialItems.electronicsScreens) gross.bubbleRolls += 1;
    if (input.specialItems.mirrorsArtwork) gross.largeBoxes += 2;
    const net = Object.fromEntries(KEYS.map((key) => [key, Math.max(0, gross[key] - input.existingSupplies[key])]));
    return { input, gross, net };
  }
  function read(key) { try { return JSON.parse(localStorage.getItem(STORAGE[key]) || 'null'); } catch { return null; } }
  function write(key, value) { try { localStorage.setItem(STORAGE[key], JSON.stringify(value)); return true; } catch { return false; } }
  function textNode(tag, text, className) { const node = document.createElement(tag); node.textContent = text; if (className) node.className = className; return node; }
  function parseConfig(element) { try { return JSON.parse(element.querySelector('[data-tms-move-config]').textContent); } catch { return null; } }
  function money(config, cents) { try { return new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(cents / 100); } catch { return (cents / 100).toFixed(2); } }
  function quantityLines(list, net, config) {
    list.replaceChildren();
    KEYS.filter((key) => net[key] > 0).forEach((key) => {
      const row = document.createElement('li');
      row.append(textNode('span', config.materials[key].label), textNode('strong', String(net[key])));
      list.append(row);
    });
    if (!list.childElementCount) list.append(textNode('li', config.strings.empty));
  }
  function mountPlanner(element) {
    if (instances.has(element)) return;
    const config = parseConfig(element);
    if (!config) return;
    const controller = new AbortController(); const signal = controller.signal;
    const form = element.querySelector('[data-planner-form]');
    const results = element.querySelector('[data-planner-results]');
    const list = element.querySelector('[data-plan-lines]');
    const status = element.querySelector('[data-plan-status]');
    const note = element.querySelector('[data-plan-note]');
    const total = element.querySelector('[data-plan-total]');
    const add = element.querySelector('[data-plan-add]');
    let current = null; let commercial = []; let busy = false;
    function getInput() {
      const data = new FormData(form);
      return sanitize({ moveType: data.get('moveType'), density: data.get('density'), withPet: data.has('withPet'), specialItems: Object.fromEntries(SPECIAL.map((key) => [key, data.has(`special.${key}`)])), existingSupplies: Object.fromEntries(KEYS.map((key) => [key, data.get(`existing.${key}`)])) });
    }
    function setInput(input) {
      form.elements.moveType.value = input.moveType; form.elements.density.value = input.density;
      form.elements.withPet.checked = input.withPet;
      SPECIAL.forEach((key) => { form.elements[`special.${key}`].checked = input.specialItems[key]; });
      KEYS.forEach((key) => { form.elements[`existing.${key}`].value = input.existingSupplies[key]; });
    }
    function render() {
      current = calculate(getInput());
      commercial = []; list.replaceChildren();
      KEYS.filter((key) => current.net[key] > 0).forEach((key) => {
        const material = config.materials[key];
        const variantId = Number(material.variantId);
        const packSize = Math.floor(Number(material.packSize));
        const valid = Number.isSafeInteger(variantId) && variantId > 0 && material.available === true && packSize > 0 && Number.isFinite(Number(material.price)) && Number(material.price) >= 0;
        const packs = valid ? Math.ceil(current.net[key] / packSize) : 0;
        commercial.push({ key, valid, id: variantId, quantity: packs, price: Number(material.price) * packs });
        const row = document.createElement('li'); const info = document.createElement('div'); const amount = document.createElement('div'); amount.className = 'tms-plan-line-amount';
        info.append(textNode('strong', material.label), textNode('small', `${current.net[key]} ${config.strings.units}`));
        if (valid) {
          const link = textNode('a', material.title || material.label, 'tms-plan-line-detail');
          // Shopify-rendered URLs are the only links used for commercial lines.
          if (typeof material.url === 'string' && material.url.startsWith('/')) link.href = material.url;
          info.append(link, textNode('small', `${packSize} ${config.strings.per_pack} · ${packs * packSize} ${config.strings.delivered}`));
          amount.append(textNode('strong', `${packs} ${config.strings.packs}`), textNode('small', money(config, Number(material.price) * packs)));
        } else amount.append(textNode('small', config.strings.missing));
        row.append(info, amount); list.append(row);
      });
      const canBuy = commercial.length > 0 && commercial.every((line) => line.valid);
      if (!commercial.length) list.append(textNode('li', config.strings.empty));
      add.hidden = !canBuy; add.disabled = busy;
      total.hidden = !canBuy; total.replaceChildren();
      if (canBuy) total.append(textNode('span', config.strings.total), textNode('strong', money(config, commercial.reduce((sum, line) => sum + line.price, 0))));
      note.textContent = commercial.length && !canBuy ? config.strings.unmapped : '';
      results.hidden = false;
    }
    const smartKit = element.querySelector('#SmartKit');
    if (smartKit && location.hash === '#SmartKit') smartKit.open = true;
    const draft = read('draft'); const saved = read('plan');
    if (draft || saved?.input) { setInput(sanitize(draft || saved.input)); render(); }
    form.addEventListener('submit', (event) => { event.preventDefault(); if (!form.reportValidity() || busy) return; render(); write('draft', current.input); results.focus({ preventScroll: true }); results.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }); }, { signal });
    form.addEventListener('input', () => { if (busy) return; write('draft', getInput()); if (current) render(); }, { signal });
    element.querySelector('[data-plan-save]').addEventListener('click', () => {
      if (!form.reportValidity()) return;
      render(); const ok = write('plan', { version: 2, input: current.input, savedAt: new Date().toISOString() });
      write('draft', current.input);
      const details = read('details') || {}; details.withPet = current.input.withPet; write('details', details);
      status.textContent = ok ? config.strings.saved : config.strings.storage_error;
    }, { signal });
    add.addEventListener('click', async () => {
      if (busy || !form.reportValidity()) return;
      render(); if (!commercial.length || !commercial.every((line) => line.valid)) return;
      const merged = new Map(); commercial.forEach((line) => merged.set(line.id, (merged.get(line.id) || 0) + line.quantity));
      const items = Array.from(merged, ([id, quantity]) => ({ id, quantity }));
      busy = true; add.disabled = true; add.textContent = config.strings.adding; status.textContent = '';
      try {
        const response = await fetch(config.cartAddUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items }), signal });
        if (!response.ok) throw new Error('Cart rejected the requested quantities.');
        await response.json();
        write('plan', { version: 2, input: current.input, savedAt: new Date().toISOString() });
        window.location.assign(config.cartUrl);
      } catch (error) { if (error.name !== 'AbortError') status.textContent = config.strings.error; }
      finally { busy = false; add.disabled = false; add.textContent = config.strings.add; }
    }, { signal });
    instances.set(element, () => controller.abort());
  }
  function mountMove(element) {
    if (instances.has(element)) return;
    const config = parseConfig(element); if (!config) return;
    const controller = new AbortController(); const signal = controller.signal;
    const form = element.querySelector('[data-move-details]');
    const empty = element.querySelector('[data-move-empty]');
    const savedSection = element.querySelector('[data-move-saved]');
    const status = element.querySelector('[data-move-status]');
    const pets = element.querySelector('[data-pet-list]');
    function renderSaved() {
      const saved = read('plan'); const hasPlan = saved?.version === 2 && saved?.input && typeof saved.input === 'object';
      empty.hidden = !!hasPlan; savedSection.hidden = !hasPlan;
      if (hasPlan) quantityLines(element.querySelector('[data-move-lines]'), calculate(saved.input).net, config);
    }
    renderSaved();
    const stored = read('details') || {}; const saved = read('plan');
    form.elements.date.value = typeof stored.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(stored.date) ? stored.date : '';
    form.elements.notes.value = typeof stored.notes === 'string' ? stored.notes.slice(0, 1000) : '';
    ['withPet', 'petKeep', 'petBowl', 'petQuiet', 'petCarrier'].forEach((key) => { form.elements[key].checked = key === 'withPet' && typeof stored[key] !== 'boolean' ? saved?.input?.withPet === true : stored[key] === true; });
    pets.hidden = !form.elements.withPet.checked;
    function saveDetails() {
      const details = { date: form.elements.date.value, notes: form.elements.notes.value.slice(0, 1000) };
      ['withPet', 'petKeep', 'petBowl', 'petQuiet', 'petCarrier'].forEach((key) => { details[key] = form.elements[key].checked; });
      status.textContent = write('details', details) ? config.strings.saved : config.strings.storage_error;
    }
    form.addEventListener('submit', (event) => { event.preventDefault(); saveDetails(); }, { signal });
    form.addEventListener('input', () => { pets.hidden = !form.elements.withPet.checked; saveDetails(); }, { signal });
    element.querySelector('[data-move-clear]').addEventListener('click', () => {
      try { localStorage.removeItem(STORAGE.plan); localStorage.removeItem(STORAGE.draft); renderSaved(); status.textContent = config.strings.clear_done; } catch { status.textContent = config.strings.storage_error; }
    }, { signal });
    window.addEventListener('storage', (event) => { if (event.key === STORAGE.plan) renderSaved(); }, { signal });
    instances.set(element, () => { controller.abort(); });
  }
  function mount(scope) { scope.querySelectorAll('[data-tms-planner]').forEach(mountPlanner); scope.querySelectorAll('[data-tms-my-move]').forEach(mountMove); }
  window.TMSNativeMove = { mount, calculate };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true }); else mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => { for (const [element, destroy] of instances) if (event.target.contains(element)) { destroy(); instances.delete(element); } });
})();
